import React, { useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import Fetch from 'helpers/fetch';
import { string } from 'yup';
import { SUBMISSIONS, TEMPLATES, SUPPLIES, JOBS, ADDITIONAL_ANALYSIS } from 'constants/apis';
import { ModalCenter, Button, TextField, WarningMessage, StandaloneIcon } from 'Components';
import './ShareModal.css';

const webURL = window.location.origin;

const ShareModal = (props) => {
  const [email, setEmail] = useState(null);
  const [hasEmailError, setHasEmailError] = useState(false);
  const [emailList, setEmailList] = useState([]);
  const [message, setMessage] = useState(undefined);
  const [icon, setIcon] = useState('check');

  const handleEmailChange = (e) => {
    const { value } = e.target;
    setMessage(undefined);
    setIcon('check');
    setEmail(value);
  };

  const handleEmailEnter = (e) => {
    if (e.key !== 'Enter' && e.key !== undefined) return;
    e.preventDefault();
    const validEmail = string().email(i18n.t('formsDetails:InvalidEmail'));
    if (!validEmail.isValidSync(email) || emailList.includes(email)) {
      setHasEmailError(true);
      return;
    }

    setEmailList([...emailList, email]);
    setEmail('');
  };

  const removeItem = (i) => {
    setEmailList([...emailList.slice(0, i), ...emailList.slice(i + 1)]);
    setMessage(undefined);
  };

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const shareId = props.shareId;

    const clientNumberForInternal = `${
      props.isInternalUser ? `&ClientNumber=${props.clientNumber}` : ''
    }`;

    switch (props.shareCode) {
      case 'Template':
        {
          const templatesBody = {
            ToEmail: emailList.join(','),
            ToPinLink: `${webURL}/templates?TemplateId=${shareId}${clientNumberForInternal}`,
            ToReviewLink: `${webURL}/templates/${shareId}/edit`,
            ToCreateNewJobLink: `${webURL}/templates?TemplateId=${shareId}${clientNumberForInternal}`,
          };

          Fetch.Post(`${TEMPLATES}/${shareId}/share`, templatesBody)
            .then((response) => response.json())
            .then((data) => {
              setMessage(data.Message);
              setIcon('check');
            })
            .catch((error) => {
              if (error.response) {
                error.response.text().then((text) => {
                  setMessage(text);
                  setIcon('cancel');
                });
              }
            });
        }
        break;
      case 'Submission':
        {
          const submissionBody = {
            ToEmail: emailList.join(','),
            ToReviewLink: `${webURL}/jobs?ECoCNumber=${shareId}${clientNumberForInternal}&searchType=jobs&exactMatch=false`,
          };
          Fetch.Post(`${SUBMISSIONS}/${shareId}/share`, submissionBody)
            .then((response) => response.json())
            .then((data) => {
              setMessage(data.Message);
              setIcon('check');
            })
            .catch((error) => {
              if (error.response) {
                error.response.text().then((text) => {
                  setMessage(text);
                  setIcon('cancel');
                });
              }
            });
        }
        break;
      case 'Job':
        {
          const jobBody = {
            ToEmail: emailList.join(','),
            ToReviewLink: `${webURL}/jobs/${shareId}`,
          };
          Fetch.Post(`${JOBS}/${shareId}/share`, jobBody)
            .then((response) => response.json())
            .then((data) => {
              setMessage(data.Message);
              setIcon('check');
            })
            .catch((error) => {
              if (error.response) {
                error.response.text().then((text) => {
                  setMessage(text);
                  setIcon('cancel');
                });
              }
            });
        }
        break;
      case 'AdditionalAnalysis': {
        const AABody = {
          ToEmail: emailList.join(','),
          ToReviewLink: `${webURL}/jobs?AdditionNumber=${shareId}${clientNumberForInternal}&searchType=jobs&globalSearchIndex=2&exactMatch=false`,
        };
        Fetch.Post(`${ADDITIONAL_ANALYSIS}/${shareId}/share`, AABody)
          .then(response => response.json())
          .then((data) => {
            setMessage(data.Message);
            setIcon('check');
          })
          .catch((error) => {
            if (error.response) {
              error.response.text().then((text) => {
                setMessage(text);
                setIcon('cancel');
              });
            }
          });
      }
        break;
      case 'Order': {
        const orderBody = {
          ToEmail: emailList.join(','),
          ToReviewLink: `${webURL}/supplies/${shareId}/edit`,
          ToCopyOrCreateLink: `${webURL}/supplies?OrderNumber=${shareId}${clientNumberForInternal}`,
        };
        Fetch.Post(`${SUPPLIES}/${shareId}/share`, orderBody)
          .then(response => response.json())
          .then((data) => {
            setMessage(data.Message);
            setIcon('check');
          })
          .catch((error) => {
            if (error.response) {
              error.response.text().then((text) => {
                setMessage(text);
                setIcon('cancel');
              });
            }
          });
      }
        break;
      default:
        break;
    }

    return false;
  };

  return (
    <ModalCenter show>
      <div
        className={`share-modal ${props.isMobile ? 'share-modal__mobile' : ''}`}
      >
        <div className="share-modal__container">
          <h2>
            {i18n.t('common:Share')} - {i18n.t(`common:${props.shareCode}`)} #
            {props.shareId}{' '}
          </h2>
          <div className="share-modal__container__text-field">
            <TextField
              title={i18n.t('common:email')}
              hasError={hasEmailError}
              value={email}
              onChange={handleEmailChange}
              required
            />
            <StandaloneIcon onClick={handleEmailEnter} icon="add_circle" />
          </div>
          {message && <WarningMessage icon={icon}>{message}</WarningMessage>}
          {emailList.map((item, i) => (
            <div key={item} className="share-modal__email__item">
              <span>{item}</span>
              <Button onClick={() => removeItem(i)}>
                <i className="material-icons">close</i>
              </Button>
            </div>
          ))}
          <div className="share-modal__buttons">
            <Button
              onClick={() =>
                props.setAppState({ shareCode: null, shareId: null })
              }
              className="button button--primary"
            >
              {i18n.t('common:Close')}
            </Button>
            <Button
              onClick={handleShare}
              disabled={!emailList.length > 0}
              className="button--primary"
            >
              {i18n.t('common:Share')}
            </Button>
          </div>
        </div>
      </div>
    </ModalCenter>
  );
};

ShareModal.propTypes = {
  shareId: PropTypes.number,
  shareCode: PropTypes.string,
  setAppState: PropTypes.func,
  isMobile: PropTypes.bool,
  clientNumber: PropTypes.string,
  isInternalUser: PropTypes.bool,
};

export default ShareModal;



// WEBPACK FOOTER //
// ./src/components/ShareModal/ShareModal.jsx